import React, { Fragment, ReactNode, createContext, useEffect, useState } from "react";
import Loader from "./loader";
import useAuthCheck from "./auth/userCheck";
import root from "window-or-global";
import { DynamicObject } from "../utils/types";
import { AlertValue } from "@superloop-bnsc/lib-portals";
import { useNetworkAuthStore } from "@superloop-bnsc/lib-logics";
import config from "../utils/config";
import {LogicMainContext} from "@superloop-bnsc/lib-logics";
import { useSiteMetadata } from "../hooks/useSiteMetadata";
import { FeatureFlags, LayoutsType, SiteAssets } from "@superloop-bnsc/lib-site-configs";

interface AuthContextType {
    currentUser: DynamicObject | null;
    setCurrentUser: React.Dispatch<React.SetStateAction<DynamicObject | null>>;
    isLoggedIn: boolean;
    setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
    userDataLoading: boolean;
    refetchUserData: (variables?: Partial<any>) => any;
}

interface GlobalAlertContextType {
    alertValue: AlertValue | null;
    setAlertValue: React.Dispatch<React.SetStateAction<AlertValue | null>>;
}

interface FeatureFlagContextType {
    featureFlags: FeatureFlags;
    setFeatureFlags: React.Dispatch<React.SetStateAction<FeatureFlags>>;
}

interface SiteAssetsContextType {
    siteAssets: SiteAssets;
    setSiteAssets: React.Dispatch<React.SetStateAction<SiteAssets>>;
}

interface SiteLayoutsContextType {
    siteLayouts: LayoutsType;
    setSiteLayouts: React.Dispatch<React.SetStateAction<LayoutsType>>;
}

export const AuthContext = createContext<AuthContextType>({
    currentUser: null,
    setCurrentUser: () => {},
    isLoggedIn: false,
    setIsLoggedIn: () => {},
    userDataLoading: false,
    refetchUserData: () => new Promise(() => null)
});
export const GlobalAlertContext = createContext<GlobalAlertContextType>({
    alertValue: null,
    setAlertValue: () => {}
})
export const FeatureFlagContext = createContext<FeatureFlagContextType> ({
    featureFlags: {} as FeatureFlags,
    setFeatureFlags: () => {}
})
export const SiteAssetsConfigContext = createContext<SiteAssetsContextType> ({
    siteAssets: {} as SiteAssets,
    setSiteAssets: () => {}
})
export const SiteLayoutsConfigContext = createContext<SiteLayoutsContextType> ({
    siteLayouts: {},
    setSiteLayouts: () => {}
})

interface MainProps {
    children: ReactNode;
}

const siteAssetsDefault = {
    headerLogoText: ""
}

const siteLayoutsDefault = {}

const FeatureFlagDefault: FeatureFlags = {
    showHeader: false,
    showDevicePanel: false,
    showPlanInfoPanel: false,
    showUsageDetailsPanel: false,
    showUserDetailsPanel: false,
    showAccountDetailsPanel: false,
    showSignupLink: false
}

export default function Main ({ children }: MainProps) {
    const description = "";
    const [alertValue, setAlertValue] = useState<AlertValue | null>(null);
    const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(FeatureFlagDefault);
    const [siteAssets, setSiteAssets] = useState<SiteAssets>(siteAssetsDefault);
    const [siteLayouts, setSiteLayouts] = useState<LayoutsType>(siteLayoutsDefault);
    const {load, saveUser} = useNetworkAuthStore();
    const metadata = useSiteMetadata();
    const {currentUser, setCurrentUser, isLoggedIn, isLoading: userDataLoading, refetchUserData, setIsLoggedIn} = useAuthCheck(featureFlags?.userQueryFields);

    useEffect(() => {
        if (config.authEndpoint) {
            load(config.authEndpoint);
        }
    }, []);

    useEffect(() => {
        if (!userDataLoading && currentUser) {
          saveUser(currentUser);
        }
    }, [currentUser, userDataLoading]);

    useEffect(() => {
        setFeatureFlags(JSON.parse(metadata.featureFlags));
    }, [metadata.featureFlags]);

    useEffect(() => {
        setSiteAssets(JSON.parse(metadata.siteAssets));
    }, [metadata.siteAssets]);

    useEffect(() => {
        setSiteLayouts(JSON.parse(metadata.siteLayoutConfigs));
    }, [metadata.siteLayoutConfigs]);

    if (userDataLoading || !root.document) {
        return <Loader/>;
    }

    return (
        <Fragment>
            <AuthContext.Provider value={{currentUser, setCurrentUser, isLoggedIn, userDataLoading, refetchUserData, setIsLoggedIn}}>
                <GlobalAlertContext.Provider value={{alertValue, setAlertValue}}>
                    <LogicMainContext.Provider
                        value={{
                            apiUrl: config.apiUrl,
                            authUrl: config.authUrl,
                            portalUrl: config.portalUrl
                        }}
                    >
                        <FeatureFlagContext.Provider value={{featureFlags, setFeatureFlags}}>
                            <SiteLayoutsConfigContext.Provider value={{siteLayouts, setSiteLayouts}}>
                                <SiteAssetsConfigContext.Provider value={{siteAssets, setSiteAssets}}>
                                    {children}
                                </SiteAssetsConfigContext.Provider>
                            </SiteLayoutsConfigContext.Provider>
                        </FeatureFlagContext.Provider>
                    </LogicMainContext.Provider>
                </GlobalAlertContext.Provider>
            </AuthContext.Provider>
        </Fragment>
    )
}